import React, { useState, useEffect, useCallback } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Bot, Users, Globe, CheckSquare, DollarSign, TrendingUp,
  AlertTriangle, ArrowRight, Zap, Building2, Briefcase,
  Activity, RefreshCw, Loader2, Receipt, BarChart3
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const fmt = (n, decimals = 0) =>
  n != null ? Number(n).toLocaleString('en-AU', { maximumFractionDigits: decimals }) : '—';

const MetricTile = ({ label, value, sub, icon: Icon, color = 'text-primary', iconBg = 'bg-primary/20' }) => (
  <Card className="metric-card card-glow">
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
      {sub && <div className="text-xs text-muted-foreground/70 mt-0.5">{sub}</div>}
    </CardContent>
  </Card>
);

const AccountRow = ({ account, platform }) => {
  if (platform === 'sitedesk') {
    const statusColors = {
      active: 'bg-green-900/40 text-green-400 border border-green-700',
      trialing: 'bg-blue-900/40 text-blue-400 border border-blue-700',
      canceled: 'bg-red-900/40 text-red-400 border border-red-700',
      past_due: 'bg-amber-900/40 text-amber-400 border border-amber-700',
      none: 'bg-gray-700 text-gray-400',
    };
    return (
      <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
        <div>
          <div className="text-sm font-medium">{account.business_name}</div>
          <div className="text-xs text-muted-foreground">{account.trade_type} · {account.location}</div>
        </div>
        <div className="text-right">
          <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[account.subscription_status] || statusColors.none}`}>
            {account.subscription_status}
          </span>
          {account.mrr > 0 && (
            <div className="text-xs text-emerald-400 mt-0.5">A${fmt(account.mrr)}/mo</div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
      <div>
        <div className="text-sm font-medium">{account.name}</div>
        <div className="text-xs text-muted-foreground">{account.category} · {account.location}</div>
      </div>
      <div className="text-right">
        <span className={`text-xs px-2 py-0.5 rounded-full ${account.status === 'active' ? 'bg-green-900/40 text-green-400 border border-green-700' : 'bg-gray-700 text-gray-400'}`}>
          {account.status}
        </span>
        <div className="text-xs text-muted-foreground mt-0.5">★ {account.rating}</div>
      </div>
    </div>
  );
};

const JobRow = ({ job }) => (
  <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
    <div>
      <div className="text-sm font-medium">{job.category}</div>
      <div className="text-xs text-muted-foreground">{job.provider_name} · {job.location}</div>
    </div>
    <div className="text-right">
      <div className="text-sm font-semibold text-emerald-400">KES {fmt(job.amount)}</div>
      <div className="text-xs text-primary/70">+KES {fmt(job.amount * 0.05, 0)} comm.</div>
    </div>
  </div>
);

const Dashboard = () => {
  const { currentProject, projectInfo, stats, currentProjectId } = useProject();
  const { api } = useAuth();
  const [usage, setUsage] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [platformData, setPlatformData] = useState(null);
  const [platformLoading, setPlatformLoading] = useState(true);
  const [platformError, setPlatformError] = useState(null);

  useEffect(() => {
    const fetchCore = async () => {
      if (!currentProjectId) return;
      try {
        const [usageData, alertsData, agentsData] = await Promise.all([
          api(`/api/usage/today?project_id=${currentProjectId}`),
          api(`/api/alerts?project_id=${currentProjectId}&unread_only=true`),
          api(`/api/agents/?project_id=${currentProjectId}`),
        ]);
        setUsage(usageData);
        setAlerts(alertsData.slice(0, 5));
        setAgents(agentsData);
      } catch (err) {
        console.error('Dashboard core fetch error:', err);
      }
    };
    fetchCore();
  }, [api, currentProjectId]);

  const fetchPlatformData = useCallback(async () => {
    setPlatformLoading(true);
    setPlatformError(null);
    try {
      const res = await axios.get('/api/platforms/dashboard');
      setPlatformData(res.data);
    } catch (err) {
      setPlatformError('Could not load platform data');
      console.error('Platform dashboard error:', err);
    } finally {
      setPlatformLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlatformData();
  }, [fetchPlatformData]);

  const agentStatusCounts = {
    running: agents.filter(a => a.status === 'running').length,
    idle: agents.filter(a => a.status === 'idle').length,
    error: agents.filter(a => a.status === 'error').length,
  };

  const sd = platformData?.sitedesk;
  const kl = platformData?.kazilink;

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">
            AI Control Tower <span className="text-muted-foreground text-xl">— Overview</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            KaziLink (Kenya) &amp; SiteDesk (Australia) unified view
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchPlatformData}
            disabled={platformLoading}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${platformLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <Link to="/chat">
            <Button data-testid="quick-chat-btn">
              <Zap className="w-4 h-4 mr-2" />
              Quick Command
            </Button>
          </Link>
        </div>
      </div>

      {/* ── SITEDESK SECTION ─────────────────────────────── */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xl">🇦🇺</span>
          <h2 className="text-lg font-semibold">SiteDesk</h2>
          <span className="text-xs text-muted-foreground">Perth, Western Australia · Field Service Platform</span>
          {sd && (
            <span className={`ml-auto text-xs px-2 py-0.5 rounded-full border ${
              sd.connector_mode === 'mock' ? 'bg-amber-900/30 text-amber-400 border-amber-700' :
              'bg-green-900/30 text-green-400 border-green-700'
            }`}>
              {sd.connector_mode} mode
            </span>
          )}
        </div>

        {platformLoading && !platformData ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading SiteDesk data...
          </div>
        ) : platformError ? (
          <div className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-4 py-3">
            {platformError}
          </div>
        ) : sd ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <MetricTile
                label="Total Accounts"
                value={fmt(sd.total_accounts)}
                icon={Building2}
                color="text-blue-400"
                iconBg="bg-blue-500/20"
              />
              <MetricTile
                label="Active Subscriptions"
                value={fmt(sd.active_subscriptions)}
                sub={`${fmt(sd.trialing)} trialing`}
                icon={Users}
                color="text-emerald-400"
                iconBg="bg-emerald-500/20"
              />
              <MetricTile
                label="Monthly Recurring"
                value={`A$${fmt(sd.mrr_aud)}`}
                sub={`ARR A$${fmt(sd.arr_aud)}`}
                icon={TrendingUp}
                color="text-emerald-400"
                iconBg="bg-emerald-500/20"
              />
              <MetricTile
                label="Churn Rate"
                value={`${fmt(sd.churn_rate, 1)}%`}
                sub={sd.past_due > 0 ? `${sd.past_due} past due` : 'no past-due'}
                icon={Activity}
                color={sd.churn_rate > 10 ? 'text-red-400' : 'text-amber-400'}
                iconBg={sd.churn_rate > 10 ? 'bg-red-500/20' : 'bg-amber-500/20'}
              />
            </div>
            <Card className="bg-card border-border card-glow">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-400" /> Sample Accounts
                </CardTitle>
                <Link to="/settings" className="text-xs text-muted-foreground hover:text-primary">
                  Settings →
                </Link>
              </CardHeader>
              <CardContent>
                {sd.sample_accounts?.map(a => (
                  <AccountRow key={a.account_id} account={a} platform="sitedesk" />
                ))}
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>

      {/* ── KAZILINK SECTION ──────────────────────────────── */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xl">🇰🇪</span>
          <h2 className="text-lg font-semibold">KaziLink</h2>
          <span className="text-xs text-muted-foreground">Nairobi, Kenya · Services Marketplace</span>
          {kl && (
            <span className={`ml-auto text-xs px-2 py-0.5 rounded-full border ${
              kl.connector_mode === 'mock' ? 'bg-amber-900/30 text-amber-400 border-amber-700' :
              'bg-green-900/30 text-green-400 border-green-700'
            }`}>
              {kl.connector_mode} mode
            </span>
          )}
        </div>

        {platformLoading && !platformData ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading KaziLink data...
          </div>
        ) : kl ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <MetricTile
                label="Total Providers"
                value={fmt(kl.total_providers)}
                icon={Briefcase}
                color="text-orange-400"
                iconBg="bg-orange-500/20"
              />
              <MetricTile
                label="Transactions"
                value={fmt(kl.transaction_count)}
                icon={Receipt}
                color="text-emerald-400"
                iconBg="bg-emerald-500/20"
              />
              <MetricTile
                label="Transaction Volume"
                value={`KES ${fmt(kl.total_transaction_volume_kes)}`}
                icon={DollarSign}
                color="text-emerald-400"
                iconBg="bg-emerald-500/20"
              />
              <MetricTile
                label="Commission Earned (5%)"
                value={`KES ${fmt(kl.total_commission_earned_kes)}`}
                sub={kl.db_commission_recorded_kes > 0 ? `KES ${fmt(kl.db_commission_recorded_kes)} recorded` : 'none recorded yet'}
                icon={BarChart3}
                color="text-primary"
                iconBg="bg-primary/20"
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="bg-card border-border card-glow">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-orange-400" /> Sample Providers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {kl.sample_providers?.map(p => (
                    <AccountRow key={p.provider_id} account={p} platform="kazilink" />
                  ))}
                </CardContent>
              </Card>
              <Card className="bg-card border-border card-glow">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-emerald-400" /> Recent Jobs
                  </CardTitle>
                  <span className="text-xs text-primary">5% commission per job</span>
                </CardHeader>
                <CardContent>
                  {kl.recent_jobs?.map(j => (
                    <JobRow key={j.job_id} job={j} />
                  ))}
                </CardContent>
              </Card>
            </div>
          </>
        ) : null}
      </div>

      {/* ── SYSTEM METRICS ────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Today's Usage */}
        <Card className="bg-card border-border card-glow">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-primary" />
              Today's AI Usage
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {usage ? (
              <>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Tokens</span>
                    <span>{(usage.tokens_used || 0).toLocaleString()} / {(usage.caps?.tokens || 100000).toLocaleString()}</span>
                  </div>
                  <Progress value={(usage.tokens_used / (usage.caps?.tokens || 100000)) * 100} className="h-1.5" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Images</span>
                    <span>{usage.images_generated || 0} / {usage.caps?.images || 30}</span>
                  </div>
                  <Progress value={(usage.images_generated / (usage.caps?.images || 30)) * 100} className="h-1.5" />
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">No usage data yet</div>
            )}
          </CardContent>
        </Card>

        {/* Pending Approvals */}
        <Card className="bg-card border-border card-glow">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckSquare className="w-4 h-4 text-primary" />
              Pending Approvals
            </CardTitle>
            {stats?.pending_approvals > 0 && (
              <Link to="/approvals">
                <Button variant="ghost" size="sm" className="text-xs">
                  View All <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            )}
          </CardHeader>
          <CardContent>
            {stats?.pending_approvals > 0 ? (
              <div>
                <div className="text-3xl font-bold text-amber-400">{stats.pending_approvals}</div>
                <p className="text-sm text-muted-foreground">actions require review</p>
              </div>
            ) : (
              <div className="text-center py-4">
                <CheckSquare className="w-8 h-8 mx-auto text-emerald-400 mb-2" />
                <p className="text-sm text-muted-foreground">All caught up!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card className="bg-card border-border card-glow">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-primary" />
              Recent Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {alerts.length > 0 ? (
              <div className="space-y-2">
                {alerts.map((alert) => (
                  <div
                    key={alert.alert_id}
                    className={`p-2 rounded text-sm ${
                      alert.severity === 'error' ? 'bg-red-500/10 text-red-400' :
                      alert.severity === 'warning' ? 'bg-amber-500/10 text-amber-400' :
                      'bg-blue-500/10 text-blue-400'
                    }`}
                  >
                    {alert.title}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-sm text-muted-foreground">No alerts</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Agent Status */}
      {agents.length > 0 && (
        <Card className="bg-card border-border card-glow">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Bot className="w-4 h-4 text-primary" />
              Agent Status
              <span className="text-xs font-normal text-muted-foreground ml-1">
                {agentStatusCounts.running} running · {agentStatusCounts.error} errors
              </span>
            </CardTitle>
            <Link to="/agents">
              <Button variant="outline" size="sm">
                Manage <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {agents.map((agent) => (
                <div key={agent.agent_id} className={`agent-card ${agent.status}`} data-testid={`agent-status-${agent.agent_type}`}>
                  <div className="flex items-center justify-between mb-2">
                    <Bot className="w-4 h-4 text-muted-foreground" />
                    <span className={`pulse-dot ${
                      agent.status === 'running' ? 'active' :
                      agent.status === 'paused' ? 'warning' :
                      agent.status === 'error' ? 'error' : 'idle'
                    }`} />
                  </div>
                  <div className="text-xs font-medium truncate">{agent.name.replace(' Agent', '')}</div>
                  <div className="text-xs text-muted-foreground capitalize">{agent.status}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
